import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Chip, FormControl, Grid, IconButton, InputAdornment,
  InputLabel, MenuItem, Select, Stack, TablePagination, TextField, Tooltip, Typography,
} from "@mui/material";
import { useState } from "react";
import {
  DifficultyChip, EmptyState, FilterBarBox, LoadingBlock, PALETTE, SectionPaper, filterSelectSx,
} from "../components/shared";
import CourseLangLogo from "../components/CourseLangLogo";
import { useSkillTests } from "../hooks/useSkillTests";
import SkillTestSubmissionsView from "./SkillTestSubmissionsView";

const CARD_MIN_HEIGHT = 300;

type Props = { onOpenTest: (testId: string) => void };

type Screen = "list" | "test-submissions";

export default function SkillTestsView({ onOpenTest }: Props) {
  const st = useSkillTests();
  const [draft, setDraft] = useState(st.filters);
  const [screen, setScreen] = useState<Screen>("list");
  const [selectedTest, setSelectedTest] = useState<any>(null);

  const apply = (next: typeof draft) => { setDraft(next); st.applyFilters(next); };
  const clear = () => apply({ search: "", category: "", language: "", topic: "", difficulty: "" });
  const hasFilters = Object.values(draft).some(Boolean);

  const openTestSubmissions = (test: any) => {
    setSelectedTest(test);
    setScreen("test-submissions");
  };

  const handleBack = () => {
    setScreen("list");
    setSelectedTest(null);
  };

  if (screen === "test-submissions" && selectedTest) {
    return (
      <SkillTestSubmissionsView
        testId={selectedTest.id ?? selectedTest._id}
        studentId={st.studentId}
        testMeta={selectedTest}
        onBack={handleBack}
        onOpenTest={onOpenTest}
        backLabel="Back to Skill Tests"
      />
    );
  }

  return (
    <Box sx={{ fontSize: 13, maxWidth: 1320, mx: "auto" }}>
      <FilterBarBox sx={{ flexWrap: "wrap", gap: 1, py: 1.25, mb: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "primary.main", whiteSpace: "nowrap" }}>Filters</Typography>
        <TextField
          size="small"
          placeholder="Search tests..."
          value={draft.search}
          onChange={(e) => setDraft((p) => ({ ...p, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && apply(draft)}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} /></InputAdornment>,
              endAdornment: draft.search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => apply({ ...draft, search: "" })}><ClearIcon sx={{ fontSize: 13 }} /></IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: 12, borderRadius: "8px", bgcolor: "#fff" },
            },
          }}
          sx={{ flex: "1 1 160px", minWidth: 140, maxWidth: 240, "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }}
        />

        {[
          ["category", "Category", st.facets.categories ?? []],
          ["language", "Language", st.facets.programmingLanguages ?? st.facets.languages ?? []],
          // ["topic", "Topic", st.facets.topics ?? []],
          ["difficulty", "Difficulty", ["beginner", "intermediate", "advanced"]],
        ].map(([key, label, opts]: any) => (
          <FormControl size="small" sx={{ flex: "0 1 120px", minWidth: 100 }} key={key}>
            <InputLabel sx={{ fontSize: 12 }}>{label}</InputLabel>
            <Select label={label} value={(draft as any)[key]}
              onChange={(e) => apply({ ...draft, [key]: e.target.value })}
              sx={{ ...filterSelectSx, fontSize: 12 }}
              MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}>
              <MenuItem value="" sx={{ fontSize: 12 }}>All</MenuItem>
              {opts.map((o: string) => (
                <MenuItem key={o} value={o} sx={{ fontSize: 12, textTransform: "capitalize" }}>{o}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: 0, md: "auto" }, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary, whiteSpace: "nowrap" }}>{st.total} tests</Typography>
          {hasFilters && (
            <Chip label="Clear" size="small" onDelete={clear} onClick={clear}
              sx={{ fontSize: 11, bgcolor: "#fff0f0", color: "#c62828", borderColor: "#ffcdd2" }} variant="outlined" />
          )}
          <Button size="small" variant="contained" onClick={() => apply(draft)}
            sx={{ textTransform: "none", fontSize: 12, height: 30, borderRadius: "8px" }}>Search</Button>
        </Box>
      </FilterBarBox>

      {st.loading ? (
        <LoadingBlock message="Loading skill tests..." />
      ) : st.tests.length === 0 ? (
        <EmptyState message="No skill tests found. Try adjusting filters." />
      ) : (
        <>
          <Grid container spacing={1.5}>
            {st.tests.map((test: any) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={test.id} sx={{ display: "flex" }}>
                <SkillTestCard
                  test={test}
                  onStart={() => onOpenTest(test.id)}
                  onViewSubmissions={() => openTestSubmissions(test)}
                />
              </Grid>
            ))}
          </Grid>
          <TablePagination
            component="div"
            count={st.total}
            page={st.page}
            rowsPerPage={12}
            rowsPerPageOptions={[12]}
            onPageChange={(_e, p) => st.changePage(p)}
            sx={{
              mt: 1.25, borderRadius: "12px", border: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight,
              "& .MuiTablePagination-toolbar": { minHeight: 40 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 },
            }}
          />
        </>
      )}
    </Box>
  );
}

function SkillTestCard({ test, onStart, onViewSubmissions }: {
  test: any; onStart: () => void; onViewSubmissions: () => void;
}) {
  const description = test.description || "Assess your skills with timed questions and instant feedback.";

  return (
    <SectionPaper sx={{
      width: "100%", minHeight: CARD_MIN_HEIGHT, height: "100%", display: "flex", flexDirection: "column",
      cursor: "pointer", transition: "box-shadow 0.15s, transform 0.15s",
      "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
    }}>
      <Box sx={{
        position: "relative", height: 68, flexShrink: 0,
        background: "#eff6ff",
        borderBottom: `1px solid ${PALETTE.border}`, px: 1.5, py: 1.25,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <CourseLangLogo language={test.programmingLanguage} category={test.category} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            {test.hasCertificate && (
              <Tooltip title="Certificate available"><SchoolOutlinedIcon sx={{ fontSize: 16, color: "#f59e0b" }} /></Tooltip>
            )}
            {test.lastPassed && (
              <Chip size="small" label="Passed" sx={{ fontSize: 10, height: 20, bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 700 }} />
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", px: 1.5, pt: 1.25, pb: 1 }}>
        <Typography sx={{
          fontWeight: 800, fontSize: 14, color: "primary.main", lineHeight: 1.35, mb: 0.5,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 38,
        }}>
          {test.title}
        </Typography>
        <Typography sx={{
          fontSize: 11, color: PALETTE.textSecondary, lineHeight: 1.45, mb: 1,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 32,
        }}>
          {description}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1, minHeight: 24, minWidth: 0, overflow: "hidden" }}>
          {test.difficulty && (
            <Box sx={{ flexShrink: 0 }}>
              <DifficultyChip difficulty={test.difficulty} />
            </Box>
          )}
          {test.topic && (
            <Tooltip title={test.topic} arrow>
              <Chip
                size="small"
                label={test.topic}
                sx={{
                  fontSize: 10,
                  height: 22,
                  maxWidth: "100%",
                  flex: 1,
                  minWidth: 0,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  },
                }}
              />
            </Tooltip>
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
          <MetaItem icon={<QuizOutlinedIcon sx={{ fontSize: 13 }} />} label={`${test.totalQuestions ?? 0} Qs`} />
          <MetaItem icon={<AccessTimeIcon sx={{ fontSize: 13 }} />} label={`${test.durationMinutes ?? 0}m`} />
          <MetaItem icon={<AssessmentOutlinedIcon sx={{ fontSize: 13 }} />} label={`${test.totalMarks ?? 0} marks`} />
        </Stack>

        {test.attempts > 0 && (
          <Typography sx={{ fontSize: 11, color: PALETTE.textMuted, mt: 1 }}>
            {test.attempts} attempt{test.attempts > 1 ? "s" : ""} · Best: {test.bestScore}/{test.totalMarks}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, pt: 0, mt: "auto", flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant={test.lastPassed ? "outlined" : "contained"} fullWidth onClick={(e) => { e.stopPropagation(); onStart(); }}
            startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontSize: 12, height: 32, borderRadius: "8px", fontWeight: 700 }}>
            {test.attempts > 0 ? (test.lastPassed ? "Retake" : "Try Again") : "Start Test"}
          </Button>
          <Tooltip title="View submissions">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onViewSubmissions(); }}
              sx={{ border: `1px solid ${PALETTE.borderDark}`, borderRadius: "8px", width: 32, height: 32, flexShrink: 0 }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </SectionPaper>
  );
}

function MetaItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.4}>
      <Box sx={{ color: PALETTE.textMuted, display: "flex" }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary, whiteSpace: "nowrap" }}>{label}</Typography>
    </Stack>
  );
}
